import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock3, Instagram, MapPin, MessageCircle, Phone, QrCode } from "lucide-react";
import { Nav } from "./Nav";
import { Marquee } from "./Marquee";
import { CoverModal } from "./CoverModal";
import { MAPS_URL, PHONE, logClick, openWhatsApp, parsePrice, slugify, trackEvent } from "@/lib/site";
import { useMeta } from "@/lib/seo";
import { tiersOf } from "./EventsSection";

const EASE = [0.16, 1, 0.3, 1];

const maskLine = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.05, ease: EASE, delay: 0.15 + i * 0.13 } }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.85, ease: EASE, delay: 0.5 + i * 0.12 } }),
};

export const FooterLinks = () => (
  <div className="footer-links">
    <span>Explore Soho Goa</span>
    <a href="/bollywood-nights-goa">Bollywood nights in Goa</a>
    <a href="/clubs-in-baga">Best clubs in Baga guide</a>
    <a href="/ladies-night-goa">Ladies night in Goa</a>
    <a href="/vip-table-booking-goa">VIP table booking</a>
  </div>
);

export const SeoLanding = ({ config, events, eventsLoaded }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  useMeta(config.title, config.description, config.path);
  useEffect(() => {
    if (!sessionStorage.getItem("soho_visit")) {
      sessionStorage.setItem("soho_visit", "1");
      logClick("visit");
    }
  }, []);
  useEffect(() => {
    const schema = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: config.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-faq-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById("page-faq-schema")?.remove(); };
  }, [config.path]);

  const shownEvents = eventsLoaded ? (config.eventsFilter ? events.filter(config.eventsFilter) : events) : [];

  return (
    <div className="site-shell">
      <Nav />
      <main>
        <section className="seo-hero" style={{ backgroundImage: `linear-gradient(90deg,#07050b 18%,#07050bcc 52%,#07050b55), linear-gradient(0deg,#07050b 4%,transparent 40%), url(${config.heroImage})` }}>
          <div className="hero-content">
            <motion.p className="eyebrow" variants={fadeUp} custom={0} initial="hidden" animate="show"><span className="live-dot"/> {config.eyebrow}</motion.p>
            <h1>
              <span className="mask"><motion.span variants={maskLine} custom={0} initial="hidden" animate="show">{config.h1a}</motion.span></span>
              <span className="mask"><motion.span className="serif-accent" variants={maskLine} custom={1} initial="hidden" animate="show">{config.h1b}</motion.span></span>
            </h1>
            <motion.p className="hero-copy" variants={fadeUp} custom={1} initial="hidden" animate="show">{config.intro}</motion.p>
            <motion.div className="hero-actions" variants={fadeUp} custom={2} initial="hidden" animate="show">
              <button className="button button-pink" onClick={() => openWhatsApp(config.whatsappMessage)} data-testid="seo-whatsapp-button"><MessageCircle size={17}/> {config.ctaLabel}</button>
              <a href={MAPS_URL} target="_blank" rel="noreferrer" className="button button-ghost" onClick={() => { trackEvent("directions_click"); logClick("directions"); }} data-testid="seo-directions-button"><MapPin size={17}/> Get directions</a>
            </motion.div>
          </div>
        </section>
        <Marquee />
        <section className="seo-content">
          {config.sections.map((section, i) => (
            <motion.div className="seo-block" key={i}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, ease: EASE, delay: 0.05 * i }}>
              <span className="num">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h2>{section.heading}</h2>
                {section.body.map((paragraph, j) => <p key={j}>{paragraph}</p>)}
                {section.bullets && <ul>{section.bullets.map((b, k) => <li key={k}>{b}</li>)}</ul>}
              </div>
            </motion.div>
          ))}
        </section>
        {shownEvents.length > 0 && (
          <section className="event-section seo-events">
            <div className="section-head">
              <div>
                <p className="chapter"><b>★</b> Upcoming at Soho Goa</p>
                <h2>{config.eventsHeading.split(" ").slice(0, -1).join(" ")} <em>{config.eventsHeading.split(" ").slice(-1)}</em></h2>
              </div>
            </div>
            <div className="event-grid">
              {shownEvents.slice(0, 3).map((event, i) => (
                <motion.article className="event-card" key={event.id} data-testid={`seo-event-card-${event.id}`}
                  initial={{ opacity: 0, y: 54 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }}>
                  <a className="event-photo" href={`/events/${slugify(event.title)}`}>
                    <img src={event.image} alt={`${event.title} at Soho Goa nightclub Baga`} loading="lazy"/>
                    <div className="date-block"><strong>{event.date}</strong><span>{event.month}</span></div>
                  </a>
                  <div className="event-info">
                    <p className="event-weekday">{event.weekday}</p>
                    <h3><a href={`/events/${slugify(event.title)}`}>{event.title}</a></h3>
                    {tiersOf(event).length > 0 && <p className="tier-line">{tiersOf(event).map((t) => `${t.label} ${t.price}`).join(" · ")}</p>}
                    <div className="event-details"><span><Clock3 size={14}/>{event.time}</span><span className="price">From ₹{(Math.min(...(tiersOf(event).map((t) => parsePrice(t.price)).filter(Boolean))) || parsePrice(event.price)).toLocaleString("en-IN")}</span></div>
                    <div className="card-actions">
                      <button className="button button-small button-pink" onClick={() => { trackEvent("cover_charge_click", { event_name: event.title }); logClick("cover_click", event.title); setSelectedEvent(event); }} data-testid={`seo-buy-cover-${event.id}`}>Get cover charge <QrCode size={14}/></button>
                      <button className="icon-btn" onClick={() => openWhatsApp(`Hi Soho Goa, I'd like to reserve a table for ${event.title}.`, event.title)} aria-label={`Reserve for ${event.title}`} data-testid={`seo-reserve-${event.id}`}><MessageCircle size={17}/></button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>
        )}
        <section className="faq-section">
          <div className="section-head"><div><p className="chapter"><b>?</b> Good to know</p><h2>Before you <em>go out.</em></h2></div></div>
          <div className="faq-list">
            {config.faqs.map((f, i) => (
              <motion.details className="faq-item" key={i} data-testid={`seo-faq-${i}`}
                initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.65, ease: EASE, delay: i * 0.07 }}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </motion.details>
            ))}
          </div>
        </section>
        <section className="seo-cta">
          <p className="kicker">{config.ctaKicker}</p>
          <h2>{config.ctaHeading} <em>tonight.</em></h2>
          <div className="hero-actions" style={{ justifyContent: "center" }}>
            <button className="button button-pink" onClick={() => openWhatsApp(config.whatsappMessage)} data-testid="seo-cta-whatsapp"><MessageCircle size={17}/> WhatsApp us</button>
            <a href={`tel:${PHONE.replaceAll(" ", "")}`} className="button button-ghost" onClick={() => { trackEvent("call_click", { phone: PHONE }); logClick("call"); }} data-testid="seo-cta-call"><Phone size={17}/> {PHONE}</a>
          </div>
        </section>
      </main>
      <footer>
        <div className="footer-top">
          <a className="brand" href="/"><img src="/logo.jpg" className="brand-logo" alt="Soho Goa nightclub Baga"/></a>
          <span>© 2026 Soho Goa. Stay out late.</span>
          <div><a href="https://instagram.com" target="_blank" rel="noreferrer"><Instagram size={18}/></a><span>All rights reserved.</span></div>
        </div>
        <FooterLinks />
        <div className="footer-mark" aria-hidden="true">SOHO</div>
      </footer>
      <CoverModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
};
